import type { components } from "@alfabetizando/contracts";
import { z } from "zod";
import { apiClient, resolveApiBaseUrl } from "../../shared/api/client";

export const pixelIdSchema = z.string().regex(/^[0-9]{5,25}$/u);
export const gtmIdSchema = z.string().regex(/^GTM-[A-Z0-9]{4,20}$/u);
export const trackingSettingsSchema = z.object({
  enabled: z.boolean(), utmify_enabled: z.boolean().optional(), meta_pixel_id: pixelIdSchema.nullable(), gtm_container_id: gtmIdSchema.nullable(),
}).strict();
export const adminTrackingSettingsSchema = trackingSettingsSchema.extend({
  meta_capi_configured: z.boolean(), meta_capi_token_fingerprint: z.string().nullable(), meta_test_event_code: z.string().max(100).nullable(),
}).strict();
export type TrackingSettings = z.infer<typeof trackingSettingsSchema>;
export type AdminTrackingSettings = z.infer<typeof adminTrackingSettingsSchema>;
export type MarketingContext = components["schemas"]["MarketingContext"];
export function marketingRequestOptions(signal?: AbortSignal) {
  const base = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL);
  return { baseUrl: base.startsWith("/") ? new URL(base, window.location.origin).toString() : base,
    credentials: "include" as const, cache: "no-store" as const, ...(signal ? { signal } : {}) };
}
export async function fetchTrackingSettings(signal?: AbortSignal): Promise<TrackingSettings> {
  const { data, response } = await apiClient.GET("/storefront/tracking", marketingRequestOptions(signal));
  if (!data || !response.ok) throw new Error("tracking_settings_unavailable");
  return trackingSettingsSchema.parse(data);
}

export async function updateStorefrontTrackingConsent(consent: boolean): Promise<boolean> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => { controller.abort(); }, 5_000);
  try {
    const { data, response } = await apiClient.POST("/storefront/tracking/consent", {
      ...marketingRequestOptions(controller.signal), keepalive: !consent, body: { consent },
    });
    return response.ok && z.object({ accepted: z.literal(true) }).strict().safeParse(data).success;
  } catch { return false; } finally { window.clearTimeout(timeout); }
}
